<template>
  <Teleport to="body">
    <div v-if="visible" class="fixed inset-0 z-[60] overflow-y-auto">
      <div class="fixed inset-0 bg-black/50 transition-opacity" @click="handleClose"></div>

      <div class="flex min-h-full items-center justify-center p-4">
        <div class="relative w-full max-w-3xl bg-white rounded-2xl shadow-xl transform transition-all max-h-[90vh] flex flex-col">
          <div class="flex items-center justify-between px-6 py-4 border-b border-slate-200 flex-shrink-0">
            <div>
              <h3 class="text-lg font-semibold text-slate-900">{{ isEdit ? '变更平台分账账户' : '创建平台分账账户' }}</h3>
              <p class="text-sm text-slate-500 mt-0.5">{{ franchiseeTitle }}</p>
            </div>
            <button
              @click="handleClose"
              class="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <div v-if="loading" class="flex items-center justify-center py-16">
            <div class="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
          </div>

          <div v-else class="flex-1 overflow-y-auto p-6">
            <div v-if="currentAccount?.receiverNo" class="mb-4 rounded-xl bg-green-50 border border-green-100 p-4">
              <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <div class="text-xs text-green-700 mb-1">接收方编号</div>
                  <div class="text-sm font-mono text-slate-900 break-all">{{ currentAccount.receiverNo }}</div>
                </div>
                <div>
                  <div class="text-xs text-green-700 mb-1">接收方状态</div>
                  <span class="status-badge" :class="receiverStatusClass">{{ receiverStatusText }}</span>
                </div>
                <div>
                  <div class="text-xs text-green-700 mb-1">更新时间</div>
                  <div class="text-sm text-slate-900">{{ currentAccount.updateTime || '-' }}</div>
                </div>
              </div>
            </div>

            <el-form
              ref="formRef"
              :model="formData"
              :rules="rules"
              label-width="130px"
              class="space-y-6"
            >
              <div class="bg-slate-50 rounded-xl p-4">
                <h4 class="text-sm font-medium text-slate-700 mb-4 flex items-center gap-2">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  基本信息
                </h4>

                <el-form-item label="接收方名称" prop="receiverName">
                  <input
                    v-model.trim="formData.receiverName"
                    type="text"
                    placeholder="请输入平台分账接收方名称"
                    class="form-input w-full"
                  >
                </el-form-item>

                <el-form-item label="联系手机" prop="contactMobile">
                  <input
                    v-model.trim="formData.contactMobile"
                    type="text"
                    placeholder="请输入联系电话"
                    class="form-input w-full"
                  >
                </el-form-item>
              </div>

              <div class="bg-slate-50 rounded-xl p-4">
                <h4 class="text-sm font-medium text-slate-700 mb-4 flex items-center gap-2">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
                  </svg>
                  收款账户信息
                </h4>

                <el-form-item label="账户类型" prop="acctTypeCode">
                  <el-select v-model="formData.acctTypeCode" class="form-select w-full">
                    <el-option label="请选择账户类型" value="" />
                    <el-option label="对公账户" value="57" />
                    <el-option label="对私账户" value="58" />
                  </el-select>
                </el-form-item>

                <el-form-item label="收款账户名" prop="acctName">
                  <input
                    v-model.trim="formData.acctName"
                    type="text"
                    placeholder="请输入收款账户名称"
                    class="form-input w-full"
                  >
                </el-form-item>

                <el-form-item label="收款账号" prop="acctNo">
                  <input
                    v-model.trim="formData.acctNo"
                    type="text"
                    placeholder="请输入收款账户卡号"
                    class="form-input w-full font-mono"
                  >
                </el-form-item>

                <div v-if="requiresManualBankInfo" class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <el-form-item label="开户行号" prop="acctOpenBankCode">
                    <input
                      v-model.trim="formData.acctOpenBankCode"
                      type="text"
                      placeholder="请输入开户行号"
                      class="form-input w-full"
                    >
                  </el-form-item>

                  <el-form-item label="清算行行号" prop="acctClearBankCode">
                    <input
                      v-model.trim="formData.acctClearBankCode"
                      type="text"
                      placeholder="请输入清算行行号"
                      class="form-input w-full"
                    >
                  </el-form-item>
                </div>

                <el-form-item v-if="requiresManualBankInfo" label="开户行名称" prop="acctOpenBankName">
                  <input
                    v-model.trim="formData.acctOpenBankName"
                    type="text"
                    placeholder="请输入开户行名称"
                    class="form-input w-full"
                  >
                </el-form-item>

                <el-form-item label="账户证件类型" prop="acctCertificateType">
                  <el-select v-model="formData.acctCertificateType" class="form-select w-full">
                    <el-option label="请选择证件类型" value="" />
                    <el-option label="身份证" value="17" />
                    <el-option label="护照" value="18" />
                    <el-option label="港澳居民来往内地通行证" value="19" />
                    <el-option label="台湾居民来往内地通行证" value="20" />
                  </el-select>
                </el-form-item>

                <el-form-item label="账户证件号" prop="acctCertificateNo">
                  <input
                    v-model.trim="formData.acctCertificateNo"
                    type="text"
                    placeholder="请输入账户证件号"
                    class="form-input w-full font-mono"
                  >
                </el-form-item>

                <el-form-item label="提现类型" prop="settleType">
                  <el-select v-model="formData.settleType" class="form-select w-full">
                    <el-option label="请选择提现类型" value="" />
                    <el-option label="主动提款" value="01" />
                    <el-option label="交易自动结算" value="03" />
                  </el-select>
                </el-form-item>
              </div>

              <div v-if="formData.acctTypeCode === '57'" class="bg-amber-50 rounded-xl p-4">
                <h4 class="text-sm font-medium text-amber-800 mb-4 flex items-center gap-2">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414A1 1 0 0119 9.414V19a2 2 0 01-2 2z"/>
                  </svg>
                  对公资料
                </h4>

                <el-form-item label="营业执照号" prop="licenseNo">
                  <input v-model.trim="formData.licenseNo" type="text" placeholder="请输入营业执照号" class="form-input w-full">
                </el-form-item>

                <el-form-item label="营业执照名" prop="licenseName">
                  <input v-model.trim="formData.licenseName" type="text" placeholder="请输入营业执照名称" class="form-input w-full">
                </el-form-item>

                <el-form-item label="法人姓名" prop="legalPersonName">
                  <input v-model.trim="formData.legalPersonName" type="text" placeholder="请输入法人姓名" class="form-input w-full">
                </el-form-item>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <el-form-item label="法人证件类型" prop="legalPersonCertificateType">
                    <el-select v-model="formData.legalPersonCertificateType" class="form-select w-full">
                      <el-option label="请选择证件类型" value="" />
                      <el-option label="身份证" value="17" />
                      <el-option label="护照" value="18" />
                      <el-option label="港澳居民来往内地通行证" value="19" />
                      <el-option label="台湾居民来往内地通行证" value="20" />
                    </el-select>
                  </el-form-item>

                  <el-form-item label="法人证件号" prop="legalPersonCertificateNo">
                    <input v-model.trim="formData.legalPersonCertificateNo" type="text" placeholder="请输入法人证件号" class="form-input w-full font-mono">
                  </el-form-item>
                </div>
              </div>
            </el-form>
          </div>

          <div class="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-2xl flex-shrink-0">
            <button @click="handleClose" class="btn-secondary" :disabled="submitting">取消</button>
            <button @click="handleSubmit" class="btn-primary" :disabled="submitting">
              {{ submitting ? '提交中...' : (isEdit ? '提交变更' : '创建接收方') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import {
  createFranchiseePlatformLakalaReceiver,
  getFranchiseePlatformLakalaReceiver,
  updateFranchiseePlatformLakalaReceiver
} from '@/api/commission'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  franchiseeId: {
    type: [Number, String],
    default: null
  },
  franchiseeInfo: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['update:visible', 'success'])

const formRef = ref(null)
const loading = ref(false)
const submitting = ref(false)
const currentAccount = ref(null)

const getInitialFormData = () => ({
  receiverName: '',
  contactMobile: '',
  licenseNo: '',
  licenseName: '',
  legalPersonName: '',
  legalPersonCertificateType: '',
  legalPersonCertificateNo: '',
  acctNo: '',
  acctName: '',
  acctTypeCode: '',
  acctCertificateType: '',
  acctCertificateNo: '',
  acctOpenBankCode: '',
  acctOpenBankName: '',
  acctClearBankCode: '',
  settleType: ''
})

const formData = reactive(getInitialFormData())

const isEdit = computed(() => !!currentAccount.value?.receiverNo)
const requiresManualBankInfo = computed(() => formData.acctTypeCode === '57')
const franchiseeTitle = computed(() => {
  const name = props.franchiseeInfo?.name
  return name ? `${name}的平台分账接收方` : '加盟商平台分账接收方'
})

const receiverStatusText = computed(() => {
  const status = currentAccount.value?.receiverStatus
  if (status === 'VALID') return '可用'
  if (status === 'INVALID') return '不可用'
  if (status === 'PENDING') return '处理中'
  return '未同步'
})

const receiverStatusClass = computed(() => {
  const status = currentAccount.value?.receiverStatus
  if (status === 'VALID') return 'bg-green-100 text-green-800'
  if (status === 'INVALID') return 'bg-rose-100 text-rose-800'
  return 'bg-amber-100 text-amber-800'
})

const rules = computed(() => {
  const baseRules = {
    receiverName: [{ required: true, message: '请输入接收方名称', trigger: 'blur' }],
    contactMobile: [
      { required: true, message: '请输入联系手机号', trigger: 'blur' },
      { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号', trigger: 'blur' }
    ],
    acctTypeCode: [{ required: true, message: '请选择账户类型', trigger: 'change' }],
    acctName: [{ required: true, message: '请输入收款账户名', trigger: 'blur' }],
    acctNo: [{ required: true, message: '请输入收款账号', trigger: 'blur' }],
    acctCertificateType: [{ required: true, message: '请选择账户证件类型', trigger: 'change' }],
    acctCertificateNo: [{ required: true, message: '请输入账户证件号', trigger: 'blur' }]
  }
  if (requiresManualBankInfo.value) {
    baseRules.acctOpenBankCode = [{ required: true, message: '请输入开户行号', trigger: 'blur' }]
    baseRules.acctOpenBankName = [{ required: true, message: '请输入开户行名称', trigger: 'blur' }]
    baseRules.acctClearBankCode = [{ required: true, message: '请输入清算行行号', trigger: 'blur' }]
  }
  if (formData.acctTypeCode === '57') {
    baseRules.licenseNo = [{ required: true, message: '请输入营业执照号', trigger: 'blur' }]
    baseRules.licenseName = [{ required: true, message: '请输入营业执照名称', trigger: 'blur' }]
    baseRules.legalPersonName = [{ required: true, message: '请输入法人姓名', trigger: 'blur' }]
    baseRules.legalPersonCertificateType = [{ required: true, message: '请选择法人证件类型', trigger: 'change' }]
    baseRules.legalPersonCertificateNo = [{ required: true, message: '请输入法人证件号', trigger: 'blur' }]
  }
  return baseRules
})

const resetForm = () => {
  const initial = getInitialFormData()
  Object.keys(initial).forEach(key => {
    const value = currentAccount.value?.[key] ?? initial[key]
    formData[key] = key === 'acctTypeCode' && value !== null && value !== undefined ? String(value) : value
  })
}

const loadAccount = async () => {
  if (!props.franchiseeId) {
    currentAccount.value = null
    resetForm()
    return
  }
  loading.value = true
  try {
    currentAccount.value = await getFranchiseePlatformLakalaReceiver(props.franchiseeId)
    resetForm()
  } catch (error) {
    currentAccount.value = null
    resetForm()
    console.error('查询平台分账账户失败:', error)
  } finally {
    loading.value = false
  }
}

const compactPayload = (payload) => {
  return Object.entries(payload).reduce((result, [key, value]) => {
    if (value === '' || value === null || value === undefined) return result
    result[key] = value
    return result
  }, {})
}

const buildSubmitData = () => {
  const payload = compactPayload({ ...formData })
  const derivedPayloadFields = [
    'tenantId',
    'lakalaCredentialMode',
    'lakalaCredentialId',
    'lakalaAppId',
    'lakalaSerialNo',
    'orgCode',
    'merInnerNo'
  ]
  derivedPayloadFields.forEach(key => {
    delete payload[key]
  })
  if (!requiresManualBankInfo.value) {
    delete payload.acctOpenBankCode
    delete payload.acctOpenBankName
    delete payload.acctClearBankCode
  }
  if (isEdit.value && currentAccount.value?.receiverStatus) {
    payload.receiverStatus = currentAccount.value.receiverStatus
  }
  return payload
}

const handleSubmit = async () => {
  try {
    if (!props.franchiseeId) {
      ElMessage.warning('缺少加盟商ID')
      return
    }
    await formRef.value.validate()
    submitting.value = true
    if (isEdit.value) {
      await updateFranchiseePlatformLakalaReceiver(props.franchiseeId, buildSubmitData())
      ElMessage.success('平台分账账户变更已提交')
    } else {
      await createFranchiseePlatformLakalaReceiver(props.franchiseeId, buildSubmitData())
      ElMessage.success('平台分账账户创建申请已提交')
    }
    emit('success')
    handleClose()
  } catch (error) {
    console.error('保存平台分账账户失败:', error)
    if (error !== false) {
      ElMessage.error(error.message || '保存失败')
    }
  } finally {
    submitting.value = false
  }
}

const handleClose = () => {
  emit('update:visible', false)
}

watch(() => [props.visible, props.franchiseeId], ([visible]) => {
  if (visible) {
    loadAccount()
  }
})
</script>
